import React, {FunctionComponent, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IStore} from '../../redux/interface';
import {useIntl} from 'react-intl';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import {CmFilters} from './CmFilters';
import {ICmFilter} from '../../redux/cm-service/interfaces';
import CmFilter from './cm-filter';
import {CmServiceFilterType} from './CmFilterEnums';
import {
  cmCountyFiltersClearedAction,
  cmCountyFiltersLoadedAction,
  selectedCmFiltersLoadedAction
} from '../../redux/cm-service/actions';
import CmFilterBar from './filter-bar';
import {CmFilterGroupsModel, CmFilterModel} from './CmFilterModels';
import {getCmCountyFiltersApi} from '../../redux/api/cmService';

import './styles.scss';

interface ICmResourceFilters {
  filters: ICmFilter[] | null
  onChange: (filters: ICmFilter[]) => void
}

export const CmResourceFilters: FunctionComponent<ICmResourceFilters> = ({
  filters,
  onChange
}: ICmResourceFilters) => {
  const {
    selectedFilters
  } = useSelector((state: IStore) => state.cm_service);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [searchStringFilter, setSearchStringFilter] = useState<ICmFilter>({} as ICmFilter);
  const [favoritesFilter, setFavoritesFilter] = useState<ICmFilter>({} as ICmFilter);

  useEffect(() => {
    if (filters) {
      const models = {...selectedFilters};

      filters.filter(f => f.format === 'LIST').forEach((filter) => {
        if (filter.name === CmServiceFilterType.cmSubcategories) {
          if (!models[filter.name]) {
            const groupedResult: CmFilterGroupsModel[] = Object.values(
              (filter.allowValues || []).reduce<Record<number, CmFilterGroupsModel>>((acc, item) => {
                if (item.parentId) {
                  if (!acc[item.parentId]) {
                    acc[item.parentId] = {
                      id: item.parentId,
                      name: item.parentName || '',
                      values: []
                    };
                  }

                  acc[item.parentId].values.push({
                    id: item.id,
                    name: item.name,
                    checked: false
                  });
                }

                return acc;
              }, {})
            );

            models[filter.name] = {
              serialNumber: filter.serialNumber,
              name: filter.name,
              groups: groupedResult,
              expanded: false
            };
          }
        } else if (!models[filter.name]) {
          models[filter.name] = {
            serialNumber: filter.serialNumber,
            name: filter.name,
            values: filter.allowValues ? filter.allowValues.map(v => ({id: v.id, name: v.name, checked: false})) : [],
            expanded: false
          };
        }
      });

      if (!searchStringFilter.name) {
        setSearchStringFilter(filters.filter(f => f.name === CmServiceFilterType.cmSearch)[0]);
      }

      if (!favoritesFilter.name) {
        setFavoritesFilter(filters.filter(f => f.name === CmServiceFilterType.cmFavorites)[0]);
      }

      dispatch(selectedCmFiltersLoadedAction(models));
    }
  }, [filters]);

  const triggerOnChange = (filterRecord: Record<string, CmFilterModel>) => {
    const cmFilters = Object.values(filterRecord).reduce((acc, f) => {
      const checkedValues = f.values?.filter(v => v.checked).map(v => v.id).join(',')
        || f.groups?.flatMap(g => g.values).filter(v => v.checked).map(v => v.id).join(',') || '';

      if (checkedValues) {
        acc.push({
          serialNumber: f.serialNumber,
          name: f.name,
          format: 'LIST',
          inputValue: checkedValues
        });
      }

      return acc;
    }, [] as ICmFilter[]);

    onChange([...cmFilters, searchStringFilter, favoritesFilter]);
  };

  const updateCountyFilters = async (filterRecord: Record<string, CmFilterModel>) : Promise<Record<string, CmFilterModel>> => {
    try {
      const regions = filterRecord[CmServiceFilterType.cmRegions]?.values
        ?.filter(v => v.checked)
        .map(v => v.id)
        .join(',');

      if (regions) {
        const {data} = await getCmCountyFiltersApi(regions);

        dispatch(cmCountyFiltersLoadedAction(data));

        if (data && data.allowValues) {
          if (filterRecord[CmServiceFilterType.cmCounties]) {
            const updatedCounties = [
              ...filterRecord[CmServiceFilterType.cmCounties].values?.filter(v => data.allowValues?.some(fv => fv.id === v.id)) || [],
              ...data.allowValues
                .filter(fv => !filterRecord[CmServiceFilterType.cmCounties].values?.some(v => v.id === fv.id))
                .map(v => ({id: v.id, name: v.name, checked: true}))
            ];

            return {
              ...filterRecord,
              [CmServiceFilterType.cmCounties]: {
                ...filterRecord[CmServiceFilterType.cmCounties],
                values: updatedCounties
              }
            };
          }

          return {
            ...filterRecord,
            [CmServiceFilterType.cmCounties]: {
              serialNumber: data.serialNumber,
              name: data.name,
              values: data.allowValues.map(v => ({id: v.id, name: v.name, checked: true})),
              expanded: false
            }
          };
        }
      } else {
        dispatch(cmCountyFiltersClearedAction());

        const updatedFilter = {...filterRecord};

        delete updatedFilter[CmServiceFilterType.cmCounties];

        return updatedFilter;
      }
    } catch (error) {
      console.error('Error fetching county filters:', error);
    }

    return filterRecord;
  };

  const handleToggle = (type: string) => {
    return async (id: number, checked: boolean) => {
      let updatedFilters = {...selectedFilters};

      if (updatedFilters[type]) {
        updatedFilters[type].values = updatedFilters[type].values?.map(v => v.id === id ? {...v, checked} : v) ?? [];
      }

      if (type === CmServiceFilterType.cmRegions) {
        updatedFilters = await updateCountyFilters(updatedFilters);
      }

      dispatch(selectedCmFiltersLoadedAction(updatedFilters));
      triggerOnChange(updatedFilters);
    };
  };

  const handleGroupToggle = (type: string, groupId: number) => {
    return async (id: number, checked: boolean) => {
      let updatedFilters = {...selectedFilters};

      if (updatedFilters[type]) {
        updatedFilters[type].groups = updatedFilters[type].groups
          ?.map(g => g.id === groupId ? {...g, values: g.values?.map(v => v.id === id ? {...v, checked} : v)} : g) ?? [];
      }

      if (type === CmServiceFilterType.cmRegions) {
        updatedFilters = await updateCountyFilters(updatedFilters);
      }

      dispatch(selectedCmFiltersLoadedAction(updatedFilters));
      triggerOnChange(updatedFilters);
    };
  };

  const handleClear = (type: string) => {
    return () => {
      const updatedFilters = Object.assign({}, selectedFilters[type]);

      const newSelectedFilters = {
        ...selectedFilters,
        [type]: {...selectedFilters[type],
          values: updatedFilters?.values?.map(v => {
            return {...v, checked: false};
          })}
      };

      if (type === CmServiceFilterType.cmRegions) {
        delete newSelectedFilters[CmServiceFilterType.cmCounties];
      }

      dispatch(selectedCmFiltersLoadedAction(newSelectedFilters));

      const cmFilters = Object.values(newSelectedFilters).map(f => {
        return {
          serialNumber: f.serialNumber,
          name: f.name,
          format: 'LIST',
          inputValue: f.values?.filter(v => v.checked).map(v => v.id).join(',')
            || f.groups?.flatMap(g => g.values).filter(v => v.checked).map(v => v.id).join(',') || ''
        };
      });

      onChange([...cmFilters, searchStringFilter, favoritesFilter]);
    };
  };

  const handleClearGrouped = (type: string) => {
    return () => {
      const updatedFilters = Object.assign({}, selectedFilters[type]);

      const newSelectedFilters = {
        ...selectedFilters,
        [type]: {...selectedFilters[type],
          groups: updatedFilters?.groups?.map(g => {
            return {...g,
              values: g.values.map(v => {
                return {...v, checked: false};
              })};
          })}
      };

      dispatch(selectedCmFiltersLoadedAction(newSelectedFilters));

      const cmFilters = Object.values(newSelectedFilters).map(f => {
        return {
          serialNumber: f.serialNumber,
          name: f.name,
          format: 'LIST',
          inputValue: f.values?.filter(v => v.checked).map(v => v.id).join(',')
            || f.groups?.flatMap(g => g.values).filter(v => v.checked).map(v => v.id).join(',') || ''
        };
      });

      onChange([...cmFilters, searchStringFilter, favoritesFilter]);
    };
  };

  const handleClickFilter = (type: string) => {
    return () => {
      dispatch(selectedCmFiltersLoadedAction({...selectedFilters, [type]: {...selectedFilters[type], expanded: !selectedFilters[type].expanded} }));
    };
  };

  const handleChangeSearchString = (value: string) => {
    const cmFilters = Object.values(selectedFilters).map(f => {
      return {
        serialNumber: f.serialNumber,
        name: f.name,
        format: 'LIST',
        inputValue: f.values?.filter(v => v.checked).map(v => v.id).join(',') || ''
      };
    });
    const newSearchStringFilter = {...searchStringFilter, inputValue: value};

    setSearchStringFilter(newSearchStringFilter);
    onChange([...cmFilters, newSearchStringFilter, favoritesFilter]);
  };

  const handleChangeFavorites = (value: string) => {
    const cmFilters = Object.values(selectedFilters).map(f => {
      return {
        serialNumber: f.serialNumber,
        name: f.name,
        format: 'LIST',
        inputValue: f.values?.filter(v => v.checked).map(v => v.id).join(',') || ''
      };
    });
    const newFavoritesFilter = {...favoritesFilter, inputValue: value};

    setFavoritesFilter(newFavoritesFilter);
    onChange([...cmFilters, searchStringFilter, newFavoritesFilter]);
  };

  const handleClearAll = () => {
    const models = {...selectedFilters};

    delete models[CmServiceFilterType.cmCounties];
    dispatch(cmCountyFiltersClearedAction());

    if (filters) {
      filters.filter(f => f.format === 'LIST').forEach((filter) => {
        if (models[filter.name]) {
          models[filter.name] = {...models[filter.name],
            values: filter.allowValues ? filter.allowValues.map(v => ({id: v.id, name: v.name, checked: false})) : []};
        }
      });
    }
    setSearchStringFilter({...searchStringFilter, inputValue: ''});
    setFavoritesFilter({...favoritesFilter, inputValue: 'false'});
    dispatch(selectedCmFiltersLoadedAction(models));
    onChange([]);
  };

  return <div className="cm-resource-filters">
    {selectedFilters && selectedFilters[CmServiceFilterType.cmSubcategories]
      && <CmFilters key={selectedFilters[CmServiceFilterType.cmSubcategories].serialNumber}
        expanded={selectedFilters[CmServiceFilterType.cmSubcategories].expanded}
        title={intl.formatMessage({
          id: `gritx.cmresource.filter.title.${CmServiceFilterType.cmSubcategories}`,
          defaultMessage: 'What providers should we show?'
        })}
        onClick={handleClickFilter(CmServiceFilterType.cmSubcategories)}
      >
        <>
          <div className="cm-resource-filters__groups">
            {selectedFilters[CmServiceFilterType.cmSubcategories].groups?.map((g, i) => {
              return (
                <div className="cm-resource-filters__group" key={i}>
                  <div className="cm-resource-filters__group-name">{g.name}</div>
                  <div key={i} className="cm-resource-filters__filter">
                    {g.values.map(v => {
                      return (
                        <CmFilter
                          title={v.name}
                          key={v.id}
                          id={v.id}
                          checked={v.checked}
                          onToggle={handleGroupToggle(CmServiceFilterType.cmSubcategories, g.id)}/>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="cm-resource-filters__button">
            <GritxButton
              title={intl.formatMessage({
                id: 'gritx.cmresource.filter.clear',
                defaultMessage: 'Clear'
              })}
              variant={ButtonVariant.Outline}
              onClick={handleClearGrouped(CmServiceFilterType.cmSubcategories)}
            />
          </div>
        </>
      </CmFilters>}
    {selectedFilters && selectedFilters[CmServiceFilterType.cmTypeTreatments]
      && <CmFilters key={selectedFilters[CmServiceFilterType.cmTypeTreatments].serialNumber}
        expanded={selectedFilters[CmServiceFilterType.cmTypeTreatments].expanded}
        title={intl.formatMessage({
          id: `gritx.cmresource.filter.title.${CmServiceFilterType.cmTypeTreatments}`,
          defaultMessage: 'What types of service should we show?'
        })}
        onClick={handleClickFilter(CmServiceFilterType.cmTypeTreatments)}
      >
        <>
          <div className="cm-resource-filters__filter">
            {selectedFilters[CmServiceFilterType.cmTypeTreatments].values?.map(v => {
              return (
                <CmFilter
                  title={v.name}
                  key={v.id}
                  id={v.id}
                  checked={v.checked}
                  onToggle={handleToggle(CmServiceFilterType.cmTypeTreatments)}/>
              );
            })}
          </div>
          <div className="cm-resource-filters__button">
            <GritxButton
              title={intl.formatMessage({
                id: 'gritx.cmresource.filter.clear',
                defaultMessage: 'Clear'
              })}
              variant={ButtonVariant.Outline}
              onClick={handleClear(CmServiceFilterType.cmTypeTreatments)}
            />
          </div>
        </>
      </CmFilters>}
    {selectedFilters && selectedFilters[CmServiceFilterType.cmRegions]
      && <CmFilters key={selectedFilters[CmServiceFilterType.cmRegions].serialNumber}
        expanded={selectedFilters[CmServiceFilterType.cmRegions].expanded}
        title={intl.formatMessage({
          id: `gritx.cmresource.filter.title.${CmServiceFilterType.cmRegions}`,
          defaultMessage: 'What regions should we show?'
        })}
        onClick={handleClickFilter(CmServiceFilterType.cmRegions)}
      >
        <>
          <div className="cm-resource-filters__filter">
            {selectedFilters[CmServiceFilterType.cmRegions].values?.map(v => {
              return (
                <CmFilter
                  title={v.name}
                  key={v.id}
                  id={v.id}
                  checked={v.checked}
                  onToggle={handleToggle(CmServiceFilterType.cmRegions)}/>
              );
            })}
          </div>
          <div className="cm-resource-filters__button">
            <GritxButton
              title={intl.formatMessage({
                id: 'gritx.cmresource.filter.clear',
                defaultMessage: 'Clear'
              })}
              variant={ButtonVariant.Outline}
              onClick={handleClear(CmServiceFilterType.cmRegions)}
            />
          </div>
        </>
      </CmFilters>}
    {selectedFilters && selectedFilters[CmServiceFilterType.cmRegions] && !selectedFilters[CmServiceFilterType.cmCounties]
      && <CmFilters expanded={false}
        title={intl.formatMessage({
          id: `gritx.cmresource.filter.title.${CmServiceFilterType.cmCounties}`,
          defaultMessage: 'What counties should we show?'
        })}
        onClick={handleClickFilter(CmServiceFilterType.cmCounties)}
        disabled={true}
      />
    }
    {selectedFilters && selectedFilters[CmServiceFilterType.cmCounties]
      && <CmFilters key={selectedFilters[CmServiceFilterType.cmCounties].serialNumber}
        expanded={selectedFilters[CmServiceFilterType.cmCounties].expanded}
        title={intl.formatMessage({
          id: `gritx.cmresource.filter.title.${CmServiceFilterType.cmCounties}`,
          defaultMessage: 'What counties should we show?'
        })}
        onClick={handleClickFilter(CmServiceFilterType.cmCounties)}
      >
        <>
          <div className="cm-resource-filters__filter">
            {selectedFilters[CmServiceFilterType.cmCounties].values?.map(v => {
              return (
                <CmFilter
                  title={v.name}
                  key={v.id}
                  id={v.id}
                  checked={v.checked}
                  onToggle={handleToggle(CmServiceFilterType.cmCounties)}/>
              );
            })}
          </div>
          <div className="cm-resource-filters__button">
            <GritxButton
              title={intl.formatMessage({
                id: 'gritx.cmresource.filter.clear',
                defaultMessage: 'Clear'
              })}
              variant={ButtonVariant.Outline}
              onClick={handleClear(CmServiceFilterType.cmCounties)}
            />
          </div>
        </>
      </CmFilters>}
    <CmFilterBar onChangeSearchString={handleChangeSearchString} onChangeFavorites={handleChangeFavorites} onClearAll={handleClearAll}/>
  </div>;
};
